/* =========================================================
   MASH CART REWARDS
   ========================================================= */

(function () {
  'use strict';


  /* =========================================================
     SHOPIFY ROOT
     ========================================================= */

  function getRoot() {
    return (
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
    ) || '/';
  }


  /* =========================================================
     GET FRESH REWARDS CONTAINER
     IMPORTANT:
     Never keep this as a global DOM reference because the
     cart section can be replaced by Shopify.
     ========================================================= */

  function getRewardsContainer() {
    return document.getElementById('cart-rewards');
  }


  /* =========================================================
     SETTINGS
     ========================================================= */

  function getSettings() {

    const rewardsContainer =
      getRewardsContainer();

    if (!rewardsContainer) {
      return null;
    }

    return {
      spoonThreshold:
        Number(
          rewardsContainer.dataset.spoonThreshold || 3000
        ),

      shippingThreshold:
        Number(
          rewardsContainer.dataset.shippingThreshold || 5000
        ),

      spoonVariantId:
        String(
          rewardsContainer.dataset.spoonVariantId ||
          '53432488821063'
        ),

      sectionId:
        rewardsContainer.dataset.sectionId
    };
  }


  /* =========================================================
     LANGUAGE
     ========================================================= */

  function getLocale() {

    const htmlLang =
      document.documentElement.lang ||
      window.Shopify?.locale ||
      'en';

    return htmlLang
      .toLowerCase()
      .split('-')[0];
  }


  /* =========================================================
     TRANSLATIONS
     ========================================================= */

  const rewardTranslations = {

    en: {

      spoon: amount =>
        'Spend ' +
        amount +
        ' more and get a free bamboo spoon',

      shipping: amount =>
        'Free bamboo spoon unlocked! Spend ' +
        amount +
        ' more and get free shipping',

      complete:
        'Free bamboo spoon + free shipping unlocked!'
    },


    de: {

      spoon: amount =>
        'Geben Sie noch ' +
        amount +
        ' aus und erhalten Sie einen kostenlosen Bambuslöffel',

      shipping: amount =>
        'Kostenloser Bambuslöffel freigeschaltet! Geben Sie noch ' +
        amount +
        ' aus und erhalten Sie kostenlosen Versand',

      complete:
        'Kostenloser Bambuslöffel + kostenloser Versand freigeschaltet!'
    },


    nl: {

      spoon: amount =>
        'Besteed nog ' +
        amount +
        ' en ontvang een gratis bamboelepel',

      shipping: amount =>
        'Gratis bamboelepel ontgrendeld! Besteed nog ' +
        amount +
        ' en ontvang gratis verzending',

      complete:
        'Gratis bamboelepel + gratis verzending ontgrendeld!'
    },


    fr: {

      spoon: amount =>
        'Dépensez encore ' +
        amount +
        ' et recevez une cuillère en bambou gratuite',

      shipping: amount =>
        'Cuillère en bambou gratuite débloquée ! Dépensez encore ' +
        amount +
        ' et bénéficiez de la livraison gratuite',

      complete:
        'Cuillère en bambou gratuite + livraison gratuite débloquées !'
    }

  };


  /* =========================================================
     MONEY
     ========================================================= */

  function formatMoney(cents, currency) {

    const amount =
      Number(cents || 0) / 100;

    try {

      return new Intl.NumberFormat(
        document.documentElement.lang || undefined,
        {
          style: 'currency',
          currency: currency || 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }
      ).format(amount);

    } catch (error) {

      return (
        amount.toFixed(2) +
        ' ' +
        (currency || 'EUR')
      );
    }
  }


  /* =========================================================
     FETCH CART
     IMPORTANT:
     Includes timeout so a slow/hanging request cannot
     block reward rendering forever.
     ========================================================= */

  async function fetchCart() {

    const controller =
      new AbortController();

    const timeout =
      setTimeout(
        function () {
          controller.abort();
        },
        15000
      );


    try {

      const response =
        await fetch(
          getRoot() +
          'cart.js?reward=' +
          Date.now(),
          {
            headers: {
              Accept: 'application/json'
            },

            cache: 'no-store',

            signal:
              controller.signal
          }
        );


      if (!response.ok) {
        throw new Error(
          'Unable to fetch cart.'
        );
      }


      return response.json();

    } catch (error) {

      if (
        error &&
        error.name === 'AbortError'
      ) {

        throw new Error(
          'Cart request timed out.'
        );
      }

      throw error;

    } finally {

      clearTimeout(timeout);
    }
  }


  /* =========================================================
     SPOON ITEMS
     ========================================================= */

  function getSpoonItems(
    cart,
    spoonVariantId
  ) {

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return [];
    }

    return cart.items.filter(
      item =>
        String(item.variant_id) ===
        String(spoonVariantId)
    );
  }


  /* =========================================================
     ELIGIBLE SUBTOTAL
     ========================================================= */

  function getEligibleSubtotal(
    cart,
    spoonVariantId
  ) {

    if (
      !cart ||
      !Array.isArray(cart.items)
    ) {
      return 0;
    }

    return cart.items.reduce(
      (total, item) => {

        /*
         * Free spoon is NEVER included
         * in reward calculation.
         */

        if (
          String(item.variant_id) ===
          String(spoonVariantId)
        ) {
          return total;
        }

        return (
          total +
          Number(
            item.final_line_price ??
            item.line_price ??
            0
          )
        );

      },
      0
    );
  }


  /* =========================================================
     RENDER REWARD MESSAGE
     ========================================================= */

  function renderRewardMessage(cart) {

    const container =
      getRewardsContainer();

    if (!container) {
      return;
    }

    const settings =
      getSettings();

    if (!settings) {
      return;
    }


    const spoonReward =
      container.querySelector(
        '#cart-reward-spoon'
      );

    const shippingReward =
      container.querySelector(
        '#cart-reward-shipping'
      );

    const completeReward =
      container.querySelector(
        '#cart-reward-complete'
      );

    const spoonMessage =
      container.querySelector(
        '[data-spoon-message]'
      );

    const shippingMessage =
      container.querySelector(
        '[data-shipping-message]'
      );

    const completeMessage =
      container.querySelector(
        '[data-complete-message]'
      );


    if (
      !spoonReward ||
      !shippingReward ||
      !completeReward
    ) {
      return;
    }


    /*
     * Hide all states first.
     */

    spoonReward.classList.remove(
      'is-visible'
    );

    shippingReward.classList.remove(
      'is-visible'
    );

    completeReward.classList.remove(
      'is-visible'
    );


    /*
     * Calculate subtotal.
     */

    const subtotal =
      getEligibleSubtotal(
        cart,
        settings.spoonVariantId
      );


    const currency =
      cart.currency || 'EUR';


    const locale =
      getLocale();


    const translation =
      rewardTranslations[locale] ||
      rewardTranslations.en;


    /* =====================================================
       BELOW €30
       ===================================================== */

    if (
      subtotal <
      settings.spoonThreshold
    ) {

      const remaining =
        settings.spoonThreshold -
        subtotal;


      if (spoonMessage) {

        spoonMessage.textContent =
          translation.spoon(
            formatMoney(
              remaining,
              currency
            )
          );
      }


      spoonReward.classList.add(
        'is-visible'
      );

      return;
    }


    /* =====================================================
       €30 - €49.99
       ===================================================== */

    if (
      subtotal >=
        settings.spoonThreshold &&
      subtotal <
        settings.shippingThreshold
    ) {

      const remaining =
        settings.shippingThreshold -
        subtotal;


      if (shippingMessage) {

        shippingMessage.textContent =
          translation.shipping(
            formatMoney(
              remaining,
              currency
            )
          );
      }


      shippingReward.classList.add(
        'is-visible'
      );

      return;
    }


    /* =====================================================
       €50+
       ===================================================== */

    if (completeMessage) {

      completeMessage.textContent =
        translation.complete;
    }


    completeReward.classList.add(
      'is-visible'
    );
  }


  /* =========================================================
     CART REQUEST
     ========================================================= */

  async function cartRequest(
    endpoint,
    body
  ) {

    const settings =
      getSettings();

    if (!settings) {
      throw new Error(
        'Cart reward container not found.'
      );
    }


    const controller =
      new AbortController();


    const timeout =
      setTimeout(
        function () {
          controller.abort();
        },
        15000
      );


    try {

      const response =
        await fetch(
          getRoot() + endpoint,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              Accept:
                'application/json'
            },

            body: JSON.stringify({

              ...body,

              sections:
                settings.sectionId,

              sections_url:
                window.location.pathname

            }),

            signal:
              controller.signal
          }
        );


      let data;


      try {

        data =
          await response.json();

      } catch (error) {

        throw new Error(
          'Invalid Shopify cart response.'
        );
      }


      if (!response.ok) {

        throw new Error(
          data.description ||
          data.message ||
          'Shopify cart request failed.'
        );
      }


      return data;

    } catch (error) {

      if (
        error &&
        error.name === 'AbortError'
      ) {

        throw new Error(
          'Shopify cart request timed out.'
        );
      }

      throw error;

    } finally {

      clearTimeout(timeout);
    }
  }


  /* =========================================================
     SECTION REPLACEMENT
     ========================================================= */

  function replaceCartSection(
    sections
  ) {

    const settings =
      getSettings();

    if (
      !settings ||
      !sections ||
      !sections[settings.sectionId]
    ) {
      return false;
    }


    const parser =
      new DOMParser();


    const doc =
      parser.parseFromString(
        sections[settings.sectionId],
        'text/html'
      );


    const newSection =
      doc.querySelector(
        '#shopify-section-' +
        settings.sectionId
      );


    const currentSection =
      document.querySelector(
        '#shopify-section-' +
        settings.sectionId
      );


    if (
      newSection &&
      currentSection
    ) {

      currentSection.replaceWith(
        newSection
      );

      return true;
    }


    return false;
  }


  /* =========================================================
     ADD FREE SPOON
     ========================================================= */

  async function addFreeSpoon() {

    const settings =
      getSettings();

    if (!settings) {
      return null;
    }


    const response =
      await cartRequest(
        'cart/add.js',
        {
          items: [
            {
              id:
                Number(
                  settings.spoonVariantId
                ),

              quantity: 1
            }
          ]
        }
      );


    /*
     * Shopify can return an updated cart section.
     */

    if (response.sections) {

      replaceCartSection(
        response.sections
      );
    }


    /*
     * IMPORTANT:
     * If Shopify returned the cart object directly,
     * use it immediately.
     *
     * Otherwise fetch the cart once.
     */

    if (
      response &&
      Array.isArray(response.items)
    ) {

      return response;
    }


    return fetchCart();
  }


  /* =========================================================
     REMOVE SPOON
     ========================================================= */

  async function removeSpoon(
    item
  ) {

    if (!item) {
      return null;
    }


    const response =
      await cartRequest(
        'cart/change.js',
        {
          id: item.key,
          quantity: 0
        }
      );


    if (response.sections) {

      replaceCartSection(
        response.sections
      );
    }


    if (
      response &&
      Array.isArray(response.items)
    ) {

      return response;
    }


    return fetchCart();
  }


  /* =========================================================
     SET SPOON QUANTITY = 1
     ========================================================= */

  async function setSpoonQuantityOne(
    item
  ) {

    if (!item) {
      return null;
    }


    if (
      Number(item.quantity) === 1
    ) {
      return null;
    }


    const response =
      await cartRequest(
        'cart/change.js',
        {
          id: item.key,
          quantity: 1
        }
      );


    if (response.sections) {

      replaceCartSection(
        response.sections
      );
    }


    if (
      response &&
      Array.isArray(response.items)
    ) {

      return response;
    }


    return fetchCart();
  }


  /* =========================================================
     MAIN SYNC
     ========================================================= */

  let syncRunning = false;
  let syncAgain = false;
  let syncTimer = null;


  async function synchronizeReward() {

    /*
     * Prevent competing synchronization requests.
     */

    if (syncRunning) {

      syncAgain = true;

      return;
    }


    syncRunning = true;


    try {

      /*
       * =====================================================
       * STEP 1
       *
       * Get the current cart.
       * =====================================================
       */

      let cart =
        await fetchCart();


      let settings =
        getSettings();


      if (!settings) {
        return;
      }


      /*
       * =====================================================
       * STEP 2
       *
       * RENDER IMMEDIATELY.
       *
       * This is the important fix.
       *
       * The customer sees:
       *
       * "Free bamboo spoon + free shipping unlocked!"
       *
       * immediately when the subtotal is €50+.
       *
       * We DO NOT wait for the spoon synchronization.
       * =====================================================
       */

      renderRewardMessage(
        cart
      );


      /*
       * =====================================================
       * STEP 3
       *
       * Determine current reward state.
       * =====================================================
       */

      let subtotal =
        getEligibleSubtotal(
          cart,
          settings.spoonVariantId
        );


      let spoonItems =
        getSpoonItems(
          cart,
          settings.spoonVariantId
        );


      /* =====================================================
         BELOW €30
         ===================================================== */

      if (
        subtotal <
        settings.spoonThreshold
      ) {

        /*
         * The message has already been rendered.
         *
         * Now clean up any existing spoon in the
         * background.
         */

        if (
          spoonItems.length > 0
        ) {

          for (
            const spoonItem of spoonItems
          ) {

            try {

              cart =
                await removeSpoon(
                  spoonItem
                );

            } catch (error) {

              console.error(
                '[MASH Reward] Unable to remove spoon:',
                error
              );

              /*
               * Do not block the reward UI because
               * spoon cleanup failed.
               */

              break;
            }
          }
        }


        /*
         * Refresh final state after cleanup.
         */

        try {

          cart =
            await fetchCart();

          settings =
            getSettings();

          if (settings) {

            renderRewardMessage(
              cart
            );
          }

        } catch (error) {

          console.error(
            '[MASH Reward] Final cart refresh failed:',
            error
          );
        }


        return;
      }


      /* =====================================================
         €30+
         ===================================================== */

      /*
       * Add spoon if there is no spoon.
       *
       * IMPORTANT:
       * This happens AFTER the reward message was already
       * rendered.
       */

      if (
        spoonItems.length === 0
      ) {

        try {

          cart =
            await addFreeSpoon();

          /*
           * addFreeSpoon() may replace the cart section.
           *
           * Therefore get the fresh settings/container
           * and render again using the latest cart.
           */

          settings =
            getSettings();


          if (settings) {

            renderRewardMessage(
              cart
            );
          }

        } catch (error) {

          console.error(
            '[MASH Reward] Unable to add free spoon:',
            error
          );

          /*
           * The reward message has already been displayed,
           * so a slow/failing spoon request does not hide it.
           */
        }
      }


      /*
       * Get fresh cart state.
       */

      try {

        cart =
          await fetchCart();

      } catch (error) {

        console.error(
          '[MASH Reward] Cart refresh failed:',
          error
        );

        return;
      }


      settings =
        getSettings();


      if (!settings) {
        return;
      }


      spoonItems =
        getSpoonItems(
          cart,
          settings.spoonVariantId
        );


      /* =====================================================
         REMOVE DUPLICATE SPOONS
         ===================================================== */

      if (
        spoonItems.length > 1
      ) {

        /*
         * Keep the first spoon only.
         */

        for (
          let i = 1;
          i < spoonItems.length;
          i++
        ) {

          try {

            cart =
              await removeSpoon(
                spoonItems[i]
              );

          } catch (error) {

            console.error(
              '[MASH Reward] Unable to remove duplicate spoon:',
              error
            );

            break;
          }
        }


        /*
         * Fresh cart after duplicate removal.
         */

        try {

          cart =
            await fetchCart();

        } catch (error) {

          console.error(
            '[MASH Reward] Duplicate cleanup refresh failed:',
            error
          );

          return;
        }


        settings =
          getSettings();


        if (!settings) {
          return;
        }


        spoonItems =
          getSpoonItems(
            cart,
            settings.spoonVariantId
          );
      }


      /* =====================================================
         SPOON QUANTITY ALWAYS = 1
         ===================================================== */

      if (
        spoonItems.length === 1 &&
        Number(
          spoonItems[0].quantity
        ) !== 1
      ) {

        try {

          cart =
            await setSpoonQuantityOne(
              spoonItems[0]
            );

        } catch (error) {

          console.error(
            '[MASH Reward] Unable to set spoon quantity:',
            error
          );
        }
      }


      /*
       * =====================================================
       * FINAL RENDER
       *
       * This is now only a synchronization update.
       *
       * The first render already happened immediately.
       * =====================================================
       */

      settings =
        getSettings();


      if (settings) {

        renderRewardMessage(
          cart
        );
      }


    } catch (error) {

      console.error(
        '[MASH Reward]',
        error
      );

    } finally {

      syncRunning = false;


      /*
       * If another cart event happened during
       * synchronization, run ONE more sync.
       */

      if (syncAgain) {

        syncAgain = false;


        setTimeout(
          synchronizeReward,
          150
        );
      }
    }
  }


  /* =========================================================
     SCHEDULE
     ========================================================= */

  function scheduleRewardSync(
    immediate
  ) {

    clearTimeout(
      syncTimer
    );


    syncTimer =
      setTimeout(
        synchronizeReward,
        immediate ? 0 : 300
      );
  }


  /* =========================================================
     QUANTITY CHANGE
     ========================================================= */

  document.addEventListener(
    'change',
    function (event) {

      if (
        event.target &&
        event.target.matches(
          'input[name="updates[]"], .quantity__input, input.quantity__input'
        )
      ) {

        scheduleRewardSync(
          false
        );
      }
    }
  );


  /* =========================================================
     QUANTITY BUTTON
     ========================================================= */

  document.addEventListener(
    'click',
    function (event) {

      const button =
        event.target.closest(
          'quantity-input button, .quantity__button, button[name="plus"], button[name="minus"]'
        );


      if (!button) {
        return;
      }


      setTimeout(
        function () {

          scheduleRewardSync(
            false
          );

        },
        500
      );
    }
  );


  /* =========================================================
     CART EVENTS
     ========================================================= */

  document.addEventListener(
    'cart:updated',
    function () {

      scheduleRewardSync(
        false
      );
    }
  );


  document.addEventListener(
    'cart:change',
    function () {

      scheduleRewardSync(
        false
      );
    }
  );


  /* =========================================================
     PAGE SHOW
     ========================================================= */

  window.addEventListener(
    'pageshow',
    function () {

      scheduleRewardSync(
        true
      );
    }
  );


  /* =========================================================
     INITIALIZE
     ========================================================= */

  scheduleRewardSync(
    true
  );

})();


/* =========================================================
   MASH CART COUNTDOWN
   ========================================================= */

(function () {

  function updateAllCountdowns() {

    const countdowns =
      document.querySelectorAll(
        '.mash-cart-countdown[data-countdown="true"]'
      );


    countdowns.forEach(
      function (container) {

        const timer =
          container.querySelector(
            '.mash-countdown__time'
          );


        const dateEl =
          container.querySelector(
            '.mash-countdown__date'
          );


        if (!timer) {
          return;
        }


        const now =
          new Date();


        /*
         * Target 22:00 (10 PM) today.
         */

        const cutoff =
          new Date();


        cutoff.setHours(
          22,
          0,
          0,
          0
        );


        /*
         * If current time is past 10 PM,
         * target 10 PM tomorrow.
         */

        if (
          now.getTime() >=
          cutoff.getTime()
        ) {

          cutoff.setDate(
            cutoff.getDate() + 1
          );
        }


        if (dateEl) {

          const options = {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          };


          dateEl.textContent =
            cutoff.toLocaleDateString(
              undefined,
              options
            );
        }


        const diff =
          cutoff.getTime() -
          now.getTime();


        if (diff <= 0) {

          timer.textContent =
            '00h : 00m : 00s';

          return;
        }


        const hours =
          Math.floor(
            diff / 3600000
          );


        const minutes =
          Math.floor(
            (diff % 3600000) /
            60000
          );


        const seconds =
          Math.floor(
            (diff % 60000) /
            1000
          );


        timer.textContent =
          String(hours).padStart(2, '0') +
          'h : ' +
          String(minutes).padStart(2, '0') +
          'm : ' +
          String(seconds).padStart(2, '0') +
          's';
      }
    );
  }


  /*
   * Prevent duplicate countdown intervals.
   */

  if (window.mashCountdownInterval) {

    clearInterval(
      window.mashCountdownInterval
    );
  }


  updateAllCountdowns();


  window.mashCountdownInterval =
    setInterval(
      updateAllCountdowns,
      1000
    );


  /*
   * Re-run countdown when Shopify replaces
   * the cart section.
   */

  document.addEventListener(
    'shopify:section:load',
    updateAllCountdowns
  );


  document.addEventListener(
    'cart:updated',
    updateAllCountdowns
  );

})();